import { FormHelperText, Typography } from '@mui/material';

import { use, useState } from 'react';

import { Or2Button } from '@/components/button/Or2Button';
import {
  Or2Dialog,
  type Or2DialogProps,
} from '@/components/feedback/Or2Dialog';
import { type DocumentType, graphql } from '@/gql';
import { Or2Link } from '@/components/navigation/Or2Link';
import { useMutation } from '@/hooks/mutationWithSnackbar';

import { env } from '@/env';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import { ReportQuerySelect } from './dialog/ReportQuerySelect';

export const SEND_ARTICLE_AS_EMAIL_MUTATION = graphql(`
  mutation SendArticleAsEmailMutation($params: SendArticleAsEmailInput) {
    sendArticleAsEmail(params: $params) {
      success
    }
  }
`);

const SEND_ARTICLE_AS_EMAIL_DIALOG_ARTICLE_FRAGMENT = graphql(`
  fragment sendArticleAsEmailDialogArticleFields on Article {
    _id
    title
    type
    feed {
      owner {
        ... on Group {
          _id
        }
        ... on Service {
          _id
        }
      }
    }
  }
`);

const SEND_ARTICLE_AS_EMAIL_REPORT_QUERY_FRAGMENT = graphql(`
  fragment sendArticleAsEmailReportQueryFields on ReportQuery {
    ...reportQuerySelectReportQueryFields
  }
`);

interface Props extends Omit<Or2DialogProps, 'title'> {
  article: DocumentType<typeof SEND_ARTICLE_AS_EMAIL_DIALOG_ARTICLE_FRAGMENT>;
  reportQueriesForEmailing: Array<
    DocumentType<typeof SEND_ARTICLE_AS_EMAIL_REPORT_QUERY_FRAGMENT>
  >;
}

export const SendArticleAsEmailDialog = ({
  article,
  reportQueriesForEmailing,
  open,
  onClose,
}: Props) => {
  const defaultHelpText = 'people in all your subscribing boards';

  const serviceId = article?.feed?.owner?._id;

  const [selectedQueryId, setSelectedQueryId] = useState('');
  const [helpText, setHelpText] = useState(defaultHelpText);

  const { currentUserId } = use(CurrentUserContext);

  const [sendArticleAsEmail, { loading }] = useMutation(
    SEND_ARTICLE_AS_EMAIL_MUTATION,
    {
      completionMessage: 'Email sent!',
      onCompleted: onClose,
    },
  );

  function onReportQueryIdChange(queryId: string) {
    setSelectedQueryId(queryId);

    const selectedQuery = reportQueriesForEmailing?.find(
      (query) => query._id === queryId,
    );

    if (selectedQuery) {
      setHelpText(
        `anyone who matches the '${selectedQuery?.displayName}' query`,
      );
    } else {
      setHelpText(defaultHelpText);
    }
  }

  const adminSiteUrl = `${env.NEXT_PUBLIC_ADMIN_URL}/${serviceId}/query/savedqueries`;

  return (
    <Or2Dialog
      loading={loading}
      slotProps={{
        transition: {
          onEnter: () => {
            setSelectedQueryId('');
            setHelpText(defaultHelpText);
          },
        },
      }}
      title={<i>&quot;{article.title}&quot;</i>}
      actions={
        <>
          <Or2Button color="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Or2Button>
          <Or2Button
            onClick={() =>
              sendArticleAsEmail({
                variables: {
                  params: {
                    article: article._id,
                    query: selectedQueryId,
                    user: currentUserId || '',
                    service: serviceId ?? '',
                  },
                },
              })
            }
          >
            Send
          </Or2Button>
        </>
      }
      open={open}
      onClose={onClose}
    >
      {reportQueriesForEmailing && reportQueriesForEmailing.length ? (
        <>
          <ReportQuerySelect
            disabled={loading}
            reportQueries={reportQueriesForEmailing}
            selectedQueryId={selectedQueryId}
            onReportQueryIdChange={onReportQueryIdChange}
          />
          <FormHelperText>
            This will send the article as an email to&nbsp;
            {helpText}
          </FormHelperText>
          <Typography variant="caption">
            <Or2Link href={adminSiteUrl} isExternal>
              Manage queries
            </Or2Link>
          </Typography>
        </>
      ) : (
        <Typography variant="body1">
          Send this article as an email to people in all subscribing boards?
        </Typography>
      )}
    </Or2Dialog>
  );
};
